// src/utils/dataUtils.js
/**
 * Biblioteca de funções utilitárias para preparação e processamento de dados
 * para visualizações e análises
 */

/**
 * Normaliza dados para uso em gráficos e visualizações
 * 
 * @param {Array} data - Array de objetos de dados brutos
 * @param {Object} options - Opções de normalização
 * @param {Object} options.keyMap - Mapeamento de chaves (original => nova)
 * @param {Object} options.transforms - Transformações a aplicar em campos específicos
 * @param {Array} options.excludeKeys - Chaves a excluir dos dados normalizados
 * @param {Array} options.includeKeys - Somente estas chaves serão incluídas (se definidas)
 * @param {Function} options.customTransform - Função personalizada para transformar cada item
 * @returns {Array} Dados normalizados
 */
export const normalizeData = (data, options = {}) => {
  const { 
    keyMap = {}, 
    transforms = {}, 
    excludeKeys = [], 
    includeKeys = null,
    customTransform = null 
  } = options;
  
  if (!Array.isArray(data)) {
    console.error('normalizeData recebeu dados não-array:', data);
    return [];
  }
  
  return data.map(item => {
    // Criar um novo objeto para o item normalizado
    let normalizedItem = {};
    
    // Obter todas as chaves do item original
    const keys = Object.keys(item);
    
    // Para cada chave, aplicar mapeamento e transformações
    keys.forEach(key => {
      // Verificar se a chave deve ser excluída
      if (excludeKeys.includes(key)) return;
      
      // Se includeKeys está definido, só incluir chaves específicas
      if (includeKeys !== null && !includeKeys.includes(key) && !keyMap[key]) return;
      
      // Obter o novo nome da chave (ou manter o original)
      const newKey = keyMap[key] || key;
      
      // Obter o valor original
      let value = item[key];
      
      // Aplicar transformação específica, se existir
      if (transforms[key]) {
        value = transforms[key](value, item);
      }
      
      // Adicionar ao item normalizado
      normalizedItem[newKey] = value;
    });
    
    // Aplicar transformação personalizada ao item inteiro, se existir
    if (customTransform) {
      normalizedItem = customTransform(normalizedItem, item);
    }
    
    return normalizedItem;
  });
};

/**
 * Agrupa os dados por uma chave específica e agrega os valores de outras chaves
 * 
 * @param {Array} data - Array de objetos de dados
 * @param {string} groupByKey - Chave para agrupar os dados
 * @param {Object} aggregations - Configuração das agregações (soma, média, etc.)
 * @param {boolean} includeCount - Incluir contagem de itens em cada grupo
 * @returns {Array} Dados agrupados
 */
export const groupAndAggregate = (data, groupByKey, aggregations = {}, includeCount = true) => {
  if (!Array.isArray(data) || !groupByKey) {
    return [];
  }
  
  // Criar objeto de grupos
  const groups = {};
  
  // Agrupar os dados
  data.forEach(item => {
    const groupValue = item[groupByKey];
    
    // Se o valor do grupo não existe ou é inválido, pular
    if (groupValue === undefined || groupValue === null) return;
    
    // Inicializar o grupo se necessário
    if (!groups[groupValue]) {
      groups[groupValue] = {
        [groupByKey]: groupValue,
        _count: 0
      };
    }
    
    // Incrementar contador
    groups[groupValue]._count++;
    
    // Aplicar agregações
    Object.keys(aggregations).forEach(key => {
      const { type, field = key } = aggregations[key];
      
      // Obter o valor do campo a ser agregado
      const value = parseFloat(item[field]);
      
      // Se não for um número válido, pular
      if (isNaN(value)) return;
      
      // Aplicar a agregação apropriada
      switch (type) {
        case 'sum':
          groups[groupValue][key] = (groups[groupValue][key] || 0) + value;
          break;
        case 'avg':
          // Para média, mantemos a soma e contagem para cálculo posterior
          if (!groups[groupValue][`_sum_${key}`]) {
            groups[groupValue][`_sum_${key}`] = 0;
            groups[groupValue][`_count_${key}`] = 0;
          }
          groups[groupValue][`_sum_${key}`] += value;
          groups[groupValue][`_count_${key}`]++;
          break;
        case 'min':
          groups[groupValue][key] = Math.min(groups[groupValue][key] || Infinity, value);
          break;
        case 'max':
          groups[groupValue][key] = Math.max(groups[groupValue][key] || -Infinity, value);
          break;
        default:
          // Tipo desconhecido, usar soma como padrão
          groups[groupValue][key] = (groups[groupValue][key] || 0) + value;
      }
    });
  });
  
  // Processar médias e finalizar os grupos
  const result = Object.values(groups).map(group => {
    // Calcular médias
    Object.keys(aggregations).forEach(key => {
      if (aggregations[key].type === 'avg') {
        const sum = group[`_sum_${key}`] || 0;
        const count = group[`_count_${key}`] || 0;
        
        // Calcular média
        group[key] = count > 0 ? sum / count : 0;
        
        // Remover campos temporários
        delete group[`_sum_${key}`];
        delete group[`_count_${key}`];
      }
    });
    
    // Remover contador se não solicitado
    if (!includeCount) {
      delete group._count;
    }
    
    return group;
  });
  
  return result;
};

/**
 * Calcula tendência linear para uma série de dados
 * 
 * @param {Array} data - Array de dados para análise
 * @param {string} xKey - Chave para eixo X (tempo)
 * @param {string} yKey - Chave para eixo Y (valor)
 * @returns {Object} Dados da tendência e previsão
 */
export const calculateTrend = (data, xKey, yKey) => {
  if (!Array.isArray(data) || data.length < 2) {
    return { slope: 0, intercept: 0, correlation: 0, forecast: [] };
  }
  
  // Extrair valores X e Y
  const points = data
    .filter(point => 
      point[xKey] !== undefined && 
      point[yKey] !== undefined && 
      !isNaN(parseFloat(point[yKey]))
    )
    .map(point => ({
      // Se xKey for uma data, converter para timestamp
      x: typeof point[xKey] === 'string' && !isNaN(Date.parse(point[xKey])) 
        ? new Date(point[xKey]).getTime() 
        : parseFloat(point[xKey]),
      y: parseFloat(point[yKey])
    }));
  
  // Se não houver pontos suficientes, retornar tendência neutra
  if (points.length < 2) {
    return { slope: 0, intercept: 0, correlation: 0, forecast: [] };
  }
  
  // Calcular médias
  const n = points.length;
  const sumX = points.reduce((sum, point) => sum + point.x, 0);
  const sumY = points.reduce((sum, point) => sum + point.y, 0);
  const meanX = sumX / n;
  const meanY = sumY / n;
  
  // Calcular somas necessárias para regressão linear
  let sumXY = 0;
  let sumXX = 0;
  let sumYY = 0;
  
  points.forEach(point => {
    const xDiff = point.x - meanX;
    const yDiff = point.y - meanY;
    sumXY += xDiff * yDiff;
    sumXX += xDiff * xDiff;
    sumYY += yDiff * yDiff;
  });
  
  // Calcular inclinação e intercepto
  const slope = sumXY / sumXX;
  const intercept = meanY - slope * meanX;
  
  // Calcular coeficiente de correlação
  const correlation = sumXY / Math.sqrt(sumXX * sumYY);
  
  // Gerar previsão para próximos pontos
  const lastXValue = points[points.length - 1].x;
  const xStep = (points[points.length - 1].x - points[0].x) / (points.length - 1);
  
  const forecast = Array.from({ length: 3 }, (_, i) => {
    const x = lastXValue + xStep * (i + 1);
    const y = slope * x + intercept;
    
    return { x, y };
  });
  
  return {
    slope,
    intercept,
    correlation,
    forecast
  };
};

/**
 * Detecta anomalias em uma série de dados usando o método do Z-score
 * 
 * @param {Array} data - Array de dados para análise
 * @param {string} key - Chave do valor para detectar anomalias
 * @param {number} threshold - Limiar de Z-score para considerar anomalia (padrão: 2)
 * @returns {Array} Dados com flags de anomalias
 */
export const detectAnomalies = (data, key, threshold = 2.0) => {
  if (!Array.isArray(data) || !key) {
    return [];
  }
  
  // Extrair valores
  const values = data
    .map(item => parseFloat(item[key]))
    .filter(val => !isNaN(val));
  
  // Se não houver valores suficientes, retornar dados originais
  if (values.length < 3) {
    return data.map(item => ({ ...item, isAnomaly: false }));
  }
  
  // Calcular média e desvio padrão
  const sum = values.reduce((acc, val) => acc + val, 0);
  const mean = sum / values.length;
  
  const sumSquaredDiffs = values.reduce((acc, val) => {
    const diff = val - mean;
    return acc + diff * diff;
  }, 0);
  
  const stdDev = Math.sqrt(sumSquaredDiffs / values.length);
  
  // Se o desvio padrão for muito pequeno, evitar divisão por zero
  if (stdDev < 0.0001) {
    return data.map(item => ({ ...item, isAnomaly: false }));
  }
  
  // Calcular Z-score para cada item e marcar anomalias
  return data.map(item => {
    const value = parseFloat(item[key]);
    
    if (isNaN(value)) {
      return { ...item, isAnomaly: false, zScore: null };
    }
    
    const zScore = (value - mean) / stdDev;
    const isAnomaly = Math.abs(zScore) > threshold;
    
    return {
      ...item,
      isAnomaly,
      zScore
    };
  });
};

/**
 * Aplica transformações de processamento de linguagem natural para gerar 
 * insights em linguagem simples a partir de dados
 * 
 * @param {Object} metrics - Dados de métricas e tendências
 * @returns {Array} Array de strings com insights em linguagem natural
 */
export const generateNLInsights = (metrics) => {
  // Este é um template básico que pode ser expandido com lógica mais complexa
  const insights = [];
  
  // Verificar tendências e crescimento
  if (metrics.revenue && metrics.revenue.previousPeriod) {
    const currentRevenue = metrics.revenue.value;
    const previousRevenue = metrics.revenue.previousPeriod;
    const change = ((currentRevenue - previousRevenue) / previousRevenue) * 100;
    
    if (change > 15) {
      insights.push(`Receita cresceu ${change.toFixed(1)}% em relação ao período anterior, indicando forte crescimento.`);
    } else if (change > 5) {
      insights.push(`Receita apresentou crescimento moderado de ${change.toFixed(1)}% comparado ao período anterior.`);
    } else if (change > -5) {
      insights.push(`Receita se manteve estável com variação de ${change.toFixed(1)}% em relação ao período anterior.`);
    } else {
      insights.push(`Receita caiu ${Math.abs(change).toFixed(1)}% em relação ao período anterior, indicando necessidade de atenção.`);
    }
  }
  
  // Verificar relações entre métricas (exemplo: CAC vs LTV)
  if (metrics.cac && metrics.ltv) {
    const ratio = metrics.ltv.value / metrics.cac.value;
    
    if (ratio > 3) {
      insights.push(`Relação LTV:CAC de ${ratio.toFixed(1)}:1 está excelente, sendo ideal para escalar investimentos.`);
    } else if (ratio > 1) {
      insights.push(`Relação LTV:CAC de ${ratio.toFixed(1)}:1 é positiva, mas há espaço para melhorias.`);
    } else {
      insights.push(`Relação LTV:CAC de ${ratio.toFixed(1)}:1 está abaixo do ideal, indicando necessidade de otimização.`);
    }
  }
  
  // Análise de canais (exemplo simplificado)
  if (metrics.channels && Array.isArray(metrics.channels)) {
    // Encontrar o canal de melhor desempenho
    const bestChannel = metrics.channels.reduce((best, channel) => {
      return (!best || channel.roi > best.roi) ? channel : best;
    }, null);
    
    if (bestChannel) {
      insights.push(`${bestChannel.name} é o canal com melhor ROI (${bestChannel.roi.toFixed(1)}%), considere aumentar investimentos nele.`);
    }
    
    // Verificar canais com ROI negativo
    const negativeChannels = metrics.channels.filter(channel => channel.roi < 0);
    if (negativeChannels.length > 0) {
      insights.push(`${negativeChannels.length} canais apresentam ROI negativo, considere revisar ou pausar campanhas nestes canais.`);
    }
  }
  
  return insights;
};

/**
 * Cria dados para visualização de funil a partir de dados de conversão
 * 
 * @param {Object} conversionData - Dados de conversão com contagens por estágio
 * @param {Array} stages - Ordem dos estágios do funil
 * @returns {Array} Dados formatados para visualização de funil
 */
export const createFunnelData = (conversionData, stages) => {
  if (!conversionData || !Array.isArray(stages)) {
    return [];
  }
  
  // Iniciar com o primeiro estágio
  let previousCount = conversionData[stages[0]] || 0;
  
  return stages.map((stage, index) => {
    const count = conversionData[stage] || 0;
    
    // Calcular taxa de conversão do estágio anterior, exceto para o primeiro
    const conversionRate = index > 0 
      ? (count / previousCount) * 100 
      : 100;
    
    // Atualizar contagem para próximo cálculo
    previousCount = count;
    
    return {
      stage,
      count,
      conversionRate: conversionRate.toFixed(1)
    };
  });
};

/**
 * Formata números para exibição amigável
 * 
 * @param {number} value - Valor a ser formatado
 * @param {Object} options - Opções de formatação
 * @returns {string} Valor formatado
 */
export const formatNumber = (value, options = {}) => {
  const {
    prefix = '',
    suffix = '',
    decimals = 2,
    thousandsSeparator = '.',
    decimalSeparator = ',',
    shorthand = false, // usar K, M, B para valores grandes
    currency = false,  // Se verdadeiro, formata como moeda
    percentSymbol = '%', // Símbolo percentual
    isPercent = false // Se verdadeiro, multiplica por 100 e adiciona %
  } = options;
  
  if (value === undefined || value === null || isNaN(value)) {
    return 'N/A';
  }
  
  // Converter para número
  let numValue = Number(value);
  
  // Se for percentual, multiplicar por 100
  if (isPercent) {
    numValue *= 100;
  }
  
  // Aplicar formatação para valores grandes (K, M, B)
  if (shorthand) {
    if (Math.abs(numValue) >= 1e9) {
      return `${prefix}${(numValue / 1e9).toFixed(1)}B${isPercent ? percentSymbol : suffix}`;
    }
    if (Math.abs(numValue) >= 1e6) {
      return `${prefix}${(numValue / 1e6).toFixed(1)}M${isPercent ? percentSymbol : suffix}`;
    }
    if (Math.abs(numValue) >= 1e3) {
      return `${prefix}${(numValue / 1e3).toFixed(1)}K${isPercent ? percentSymbol : suffix}`;
    }
  }
  
  // Formatar número
  const formattedValue = currency 
    ? new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(numValue)
    : new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(numValue);
  
  // Se for moeda ou percentual, não adicionar prefixo/sufixo customizado
  if (currency) {
    return formattedValue;
  }
  
  return `${prefix}${formattedValue}${isPercent ? percentSymbol : suffix}`;
};

/**
 * Converte dados para formato adequado para exportação
 * 
 * @param {Array} data - Dados a serem exportados
 * @param {Object} options - Opções de exportação
 * @returns {string} Dados formatados para exportação (CSV/JSON)
 */
export const prepareDataExport = (data, options = {}) => {
  const {
    format = 'csv',  // csv ou json
    delimiter = ',',
    includeHeaders = true,
    dateFormat = 'YYYY-MM-DD'
  } = options;
  
  if (!Array.isArray(data) || data.length === 0) {
    return '';
  }
  
  // Formatar data conforme especificado
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      
      return dateFormat
        .replace('YYYY', date.getFullYear())
        .replace('MM', String(date.getMonth() + 1).padStart(2, '0'))
        .replace('DD', String(date.getDate()).padStart(2, '0'));
    } catch (e) {
      return dateStr;
    }
  };
  
  // Para formato CSV
  if (format.toLowerCase() === 'csv') {
    // Obter todas as chaves (cabeçalhos)
    const headers = Object.keys(data[0]);
    
    // Criar linhas
    const rows = data.map(item => {
      return headers.map(header => {
        const value = item[header];
        
        // Formatar valores especiais
        if (value === null || value === undefined) {
          return '';
        } else if (typeof value === 'string') {
          // Escapar aspas duplas no texto
          const escaped = value.replace(/"/g, '""');
          return `"${escaped}"`;
        } else if (value instanceof Date) {
          return `"${formatDate(value.toISOString())}"`;
        } else if (typeof value === 'object') {
          return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        } else {
          return value;
        }
      }).join(delimiter);
    });
    
    // Adicionar cabeçalhos se necessário
    if (includeHeaders) {
      rows.unshift(headers.map(header => `"${header}"`).join(delimiter));
    }
    
    return rows.join('\n');
  }
  
  // Para formato JSON
  if (format.toLowerCase() === 'json') {
    // Processar datas no JSON
    const processedData = data.map(item => {
      const processed = {};
      
      Object.keys(item).forEach(key => {
        const value = item[key];
        
        if (value instanceof Date) {
          processed[key] = formatDate(value.toISOString());
        } else {
          processed[key] = value;
        }
      });
      
      return processed;
    });
    
    return JSON.stringify(processedData, null, 2);
  }
  
  // Formato não suportado
  return '';
};

/**
 * Calcula diferenças percentuais entre dois conjuntos de dados
 * 
 * @param {Array} currentData - Dados atuais
 * @param {Array} previousData - Dados do período anterior
 * @param {string} compareKey - Chave para associar registros entre conjuntos
 * @param {Array} metricKeys - Chaves das métricas para comparar
 * @returns {Array} Dados com diferenças percentuais calculadas
 */
export const calculateDiffs = (currentData, previousData, compareKey, metricKeys) => {
  if (!Array.isArray(currentData) || !Array.isArray(previousData)) {
    return [];
  }
  
  // Criar mapa do conjunto anterior para fácil acesso
  const previousMap = {};
  previousData.forEach(item => {
    if (item[compareKey]) {
      previousMap[item[compareKey]] = item;
    }
  });
  
  // Percorrer dados atuais e calcular diferenças
  return currentData.map(current => {
    const result = { ...current };
    
    // Buscar item correspondente no conjunto anterior
    const previous = previousMap[current[compareKey]];
    
    // Se não encontrou correspondência, não há diferença para calcular
    if (!previous) {
      metricKeys.forEach(key => {
        result[`${key}Diff`] = null;
        result[`${key}DiffPercent`] = null;
      });
      return result;
    }
    
    // Calcular diferenças para cada métrica
    metricKeys.forEach(key => {
      const currentValue = parseFloat(current[key]);
      const previousValue = parseFloat(previous[key]);
      
      // Se algum valor não é válido, não calcular diferença
      if (isNaN(currentValue) || isNaN(previousValue)) {
        result[`${key}Diff`] = null;
        result[`${key}DiffPercent`] = null;
        return;
      }
      
      // Calcular diferença absoluta
      const diff = currentValue - previousValue;
      result[`${key}Diff`] = diff;
      
      // Calcular diferença percentual (evitar divisão por zero)
      if (previousValue !== 0) {
        result[`${key}DiffPercent`] = (diff / Math.abs(previousValue)) * 100;
      } else if (diff === 0) {
        result[`${key}DiffPercent`] = 0; // Sem mudança
      } else {
        result[`${key}DiffPercent`] = diff > 0 ? Infinity : -Infinity;
      }
    });
    
    return result;
  });
};

export default {
  normalizeData,
  groupAndAggregate,
  calculateTrend,
  detectAnomalies,
  generateNLInsights,
  createFunnelData,
  formatNumber,
  prepareDataExport,
  calculateDiffs
};
